import { useMemo, useState } from "react"
import { FormProvider, useFormContext, useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { TrashSimple } from "@phosphor-icons/react"
import { useDirtyDrawerMaker } from "@/components/drawer-wrapper"
import { Button } from "@/components/ui/button"
import { Card, CardHeader, CardHeaderTitle } from "@/components/ui/card"
import {
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer"
import { FormField } from "@/components/ui/form"
import { joinStrings } from "@/lib/joinString"
import { DiagnosisSearch } from "@/modules/admin/treatment/components/sections/diagnosis/diagnosis-search"
import { TreatmentForm } from "@/modules/admin/treatment/schema/treatment-form-schema"
import { useTreamentByIdQuery$data } from "@/modules/admin/treatment/services/__generated__/useTreamentByIdQuery.graphql"
import { useDrawerStore } from "@/stores/useDrawerStore"
import { ArrElement } from "@/types/ArrElement"

type DiagnosisCodes = NonNullable<useTreamentByIdQuery$data["treatmentPlanTemplateById"]>["icdDiagnoses"]
type DiagnosisCode = ArrElement<DiagnosisCodes>

const DiagnosisDrawer = () => {
  const { t } = useTranslation()
  const closeDrawer = useDrawerStore((state) => state.closeDrawer)

  const [hasAnimationEnded, setHasAnimationEnded] = useState(false)

  const form = useFormContext<TreatmentForm>()

  const {
    formState: { isDirty },
    control,
  } = form

  const diagnosisCodes = useWatch({ control, name: "diagnosisCodes" })
  const excludedDiagnosisCodes = useMemo(() => {
    if (!diagnosisCodes || diagnosisCodes.length === 0) return []
    return diagnosisCodes.map((code) => code.diagnosisCode)
  }, [diagnosisCodes])

  const handleDrawerChange = () => {
    closeDrawer()
  }

  useDirtyDrawerMaker(isDirty)

  return (
    <Drawer open onOpenChange={handleDrawerChange}>
      <FormProvider {...form}>
        <FormField
          control={control}
          name="diagnosisCodes"
          render={({ field: { onChange, value } }) => {
            return (
              <DrawerContent data-testid="diagnosis-drawer-content" onAnimationEnd={() => setHasAnimationEnded(true)}>
                <DrawerClose className="top-2 right-2" />
                <DrawerHeader className="gap-4 pr-4">
                  <DrawerTitle className="animate-fade-in-bottom text-on-surface-subdued text-sm font-medium">
                    {t("admin.treatment.diagnosisDrawer.title")}
                  </DrawerTitle>
                  <DiagnosisSearch
                    autoFocus={hasAnimationEnded}
                    excludedIcdCodes={excludedDiagnosisCodes}
                    onOptionSelect={(diagnosisCode) => {
                      const selectedValues = [...(value ?? []), diagnosisCode]
                      onChange(selectedValues)
                    }}
                  />
                </DrawerHeader>
                <DrawerBody className="gap-4 p-4">
                  {value?.map((field) => {
                    const title = joinStrings([field.diagnosisCode, field.text], " - ")
                    return (
                      <Card
                        key={field.id}
                        className="flex h-auto flex-col gap-2 p-4 pr-2"
                        data-testid={`${field.diagnosisCode}-item`}
                        title={title}>
                        <CardHeader
                          data-testid={`${field.diagnosisCode}-detail-card-header`}
                          className="items-center gap-4 p-0">
                          <div>
                            <CardHeaderTitle className="text-base leading-6 font-medium">{title}</CardHeaderTitle>
                          </div>
                          <Button
                            variant="circle"
                            className="h-10 w-10"
                            data-testid={`${field.diagnosisCode}-delete-card-btn`}
                            onClick={() => {
                              const filteredDiagnoses = value?.filter(
                                (item) => item.diagnosisCode !== field.diagnosisCode,
                              )
                              onChange(filteredDiagnoses)
                            }}>
                            <TrashSimple className="text-on-surface-subdued" />
                          </Button>
                        </CardHeader>
                      </Card>
                    )
                  })}
                </DrawerBody>
                <DrawerFooter>
                  <div className="flex w-full flex-wrap justify-between gap-2">
                    <div className="ml-auto flex gap-2">
                      <Button variant="secondary" onClick={handleDrawerChange}>
                        {t("common.button.close")}
                      </Button>
                    </div>
                  </div>
                </DrawerFooter>
              </DrawerContent>
            )
          }}
        />
      </FormProvider>
    </Drawer>
  )
}

export { DiagnosisDrawer, type DiagnosisCode, type DiagnosisCodes }
